/** Minimal WebAudio synth — no samples, everything generated from oscillators & noise.
 *  AudioContext is created lazily on first user gesture so iPad Safari doesn't block it. */

class AudioManager {
  private ctx: AudioContext | null = null;
  private master?: GainNode;
  private muted = false;

  // Thrust rumble nodes (kept alive, gain modulated by throttle)
  private thrustNoise?: AudioBufferSourceNode;
  private thrustFilter?: BiquadFilterNode;
  private thrustGain?: GainNode;
  private thrustActive = false;

  ensure() {
    if (this.ctx) return this.ctx;
    const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    if (!AC) return null;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 0.8;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  setMuted(m: boolean) {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : 0.8;
  }

  isMuted() { return this.muted; }

  play(which: "tap" | "error" | "chime" | "thud" | "whoosh") {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    // iOS starts context in "suspended" — resume on first try
    if (ctx.state === "suspended") void ctx.resume();

    switch (which) {
      case "tap": return this.tone(ctx, 880, 0.04, "sine", 0.12);
      case "error": {
        this.tone(ctx, 220, 0.12, "square", 0.2);
        return;
      }
      case "chime": {
        this.tone(ctx, 784, 0.18, "sine", 0.18); // G5
        setTimeout(() => this.tone(ctx, 1175, 0.22, "sine", 0.18), 140); // D6
        return;
      }
      case "thud": {
        // Low-freq noise burst
        const buf = this.makeNoiseBuffer(ctx, 0.22);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const filt = ctx.createBiquadFilter();
        filt.type = "lowpass";
        filt.frequency.value = 220;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.7, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
        src.connect(filt).connect(g).connect(this.master);
        src.start();
        src.stop(ctx.currentTime + 0.3);
        return;
      }
      case "whoosh": {
        const buf = this.makeNoiseBuffer(ctx, 0.5);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const filt = ctx.createBiquadFilter();
        filt.type = "bandpass";
        filt.frequency.setValueAtTime(600, ctx.currentTime);
        filt.frequency.exponentialRampToValueAtTime(2400, ctx.currentTime + 0.4);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.06);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
        src.connect(filt).connect(g).connect(this.master);
        src.start();
        src.stop(ctx.currentTime + 0.55);
        return;
      }
    }
  }

  /** Turn thrust rumble on (call once per flight), modulate intensity via updateThrust. */
  startThrust() {
    const ctx = this.ensure();
    if (!ctx || !this.master || this.thrustActive) return;
    if (ctx.state === "suspended") void ctx.resume();

    const buf = this.makeNoiseBuffer(ctx, 2);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const filt = ctx.createBiquadFilter();
    filt.type = "lowpass";
    filt.frequency.value = 320;
    filt.Q.value = 1;
    const g = ctx.createGain();
    g.gain.value = 0;
    src.connect(filt).connect(g).connect(this.master);
    src.start();

    this.thrustNoise = src;
    this.thrustFilter = filt;
    this.thrustGain = g;
    this.thrustActive = true;
  }

  updateThrust(intensity: number) {
    if (!this.thrustGain || !this.thrustFilter || !this.ctx) return;
    const clamped = Math.max(0, Math.min(1, intensity));
    this.thrustGain.gain.setTargetAtTime(clamped * 0.35, this.ctx.currentTime, 0.08);
    this.thrustFilter.frequency.setTargetAtTime(220 + clamped * 600, this.ctx.currentTime, 0.08);
  }

  stopThrust() {
    if (!this.thrustNoise || !this.ctx) return;
    try { this.thrustNoise.stop(this.ctx.currentTime + 0.05); } catch {}
    this.thrustNoise.disconnect();
    this.thrustFilter?.disconnect();
    this.thrustGain?.disconnect();
    this.thrustNoise = undefined;
    this.thrustFilter = undefined;
    this.thrustGain = undefined;
    this.thrustActive = false;
  }

  // ---- helpers ----

  private tone(ctx: AudioContext, freq: number, dur: number, type: OscillatorType, vol: number) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.connect(gain).connect(this.master!);
    osc.start();
    osc.stop(ctx.currentTime + dur + 0.02);
  }

  private makeNoiseBuffer(ctx: AudioContext, durationS: number) {
    const sr = ctx.sampleRate;
    const len = Math.floor(sr * durationS);
    const buf = ctx.createBuffer(1, len, sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }
}

export const audio = new AudioManager();
